import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Edit, Save, Trash2, Plus, X, ExternalLink, Image, AlertCircle, Wand2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { portfolioAPI } from '../../services/api';

const AdminPortfolio = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentTech, setCurrentTech] = useState('');
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  const emptyProject = {
    id: null,
    title: '',
    description: '',
    image: '',
    tech: [],
    category: 'Web Application',
    project_url: ''
  };

  const [formData, setFormData] = useState(emptyProject);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await portfolioAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch projects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setFormData({
      ...project,
      project_url: project.project_url || ''
    });
    setEditingProject(project.id);
    setImagePreviewError(false);
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setFormData({ ...emptyProject });
    setEditingProject(null);
    setImagePreviewError(false);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "Project title is required",
        variant: "destructive"
      });
      return;
    }

    try {
      const projectData = {
        title: formData.title,
        description: formData.description,
        image: formData.image,
        tech: formData.tech,
        category: formData.category,
        project_url: formData.project_url || null
      };

      if (editingProject) {
        await portfolioAPI.update(editingProject, projectData);
        toast({
          title: "Project updated",
          description: "Changes have been saved"
        });
      } else {
        await portfolioAPI.create(projectData);
        toast({
          title: "Project added",
          description: "New project has been created"
        });
      }
      
      await fetchProjects();
      setIsDialogOpen(false);
      setFormData(emptyProject);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save project",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await portfolioAPI.delete(id);
      toast({
        title: "Project deleted",
        description: "The project has been removed"
      });
      await fetchProjects();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive"
      });
    }
  };

  const handleAddTech = () => {
    if (currentTech.trim()) {
      setFormData({
        ...formData,
        tech: [...formData.tech, currentTech.trim()]
      });
      setCurrentTech('');
    }
  };

  const handleRemoveTech = (index) => {
    setFormData({
      ...formData,
      tech: formData.tech.filter((_, i) => i !== index)
    });
  };

  const handleImageUrlChange = (url) => {
    setFormData({ ...formData, image: url });
    setImagePreviewError(false);
  };

  const generatePreviewFromUrl = () => {
    if (!formData.project_url) {
      toast({
        title: "No URL provided",
        description: "Please enter a project URL first",
        variant: "destructive"
      });
      return;
    }

    // Ensure URL has protocol
    let url = formData.project_url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
      setFormData(prev => ({ ...prev, project_url: url }));
    }

    setIsGeneratingPreview(true);
    setImagePreviewError(false);

    // Use thum.io free screenshot service
    const screenshotUrl = `https://image.thum.io/get/width/800/crop/600/${url}`;
    
    // Test if the image loads
    const img = new window.Image();
    img.onload = () => {
      setFormData(prev => ({ ...prev, image: screenshotUrl }));
      setIsGeneratingPreview(false);
      toast({
        title: "Preview generated!",
        description: "Website screenshot has been captured"
      });
    };
    img.onerror = () => {
      setIsGeneratingPreview(false);
      setImagePreviewError(true);
      toast({
        title: "Failed to generate preview",
        description: "Could not capture website screenshot. Try entering an image URL manually.",
        variant: "destructive"
      });
    };
    img.src = screenshotUrl;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-gray-400">Loading projects...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-light text-white">Portfolio Management</h1>
          <Button
            onClick={handleAddNew}
            className="bg-white text-black hover:bg-gray-200 transition-all duration-300"
          >
            <Plus size={16} className="mr-2" />
            Add Project
          </Button>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12 border border-white/10">
            <p className="text-gray-400 mb-4">No projects yet</p>
            <Button onClick={handleAddNew} className="bg-white text-black hover:bg-gray-200">
              <Plus size={16} className="mr-2" />
              Add Your First Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="border border-white/10 overflow-hidden group">
                <div className="aspect-[4/3] bg-gray-900 overflow-hidden relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                  {project.project_url && (
                    <a
                      href={project.project_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 p-2 bg-black/60 text-white hover:bg-black/80 transition-colors"
                      title="View live project"
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-light text-white mb-2">{project.title}</h3>
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.slice(0, 3).map((tech, index) => (
                      <span key={index} className="text-xs px-2 py-1 border border-white/10 text-gray-400">
                        {tech}
                      </span>
                    ))}
                    {project.tech.length > 3 && (
                      <span className="text-xs px-2 py-1 text-gray-500">
                        +{project.tech.length - 3} more
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleEdit(project)}
                      className="flex-1 bg-white/10 text-white hover:bg-white/20 border border-white/20"
                    >
                      <Edit size={14} className="mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(project.id)}
                      className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-black border border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-light">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white"
                  placeholder="Project title"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white resize-none"
                  placeholder="Project description"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                  <ExternalLink size={14} className="inline mr-2" />
                  Project URL
                </label>
                <div className="flex space-x-2">
                  <Input
                    value={formData.project_url}
                    onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
                    className="flex-1 bg-transparent border-white/10 focus:border-white text-white"
                    placeholder="https://myproject.com"
                  />
                  <Button
                    type="button"
                    onClick={generatePreviewFromUrl}
                    disabled={isGeneratingPreview || !formData.project_url}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                    title="Generate preview from URL"
                  >
                    {isGeneratingPreview ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Wand2 size={16} />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter website URL and click <Wand2 size={10} className="inline mx-1" /> to auto-generate preview image
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
                  <Image size={14} className="inline mr-2" />
                  Image URL
                </label>
                <Input
                  value={formData.image}
                  onChange={(e) => handleImageUrlChange(e.target.value)}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.project_url 
                    ? "Auto-generated from project URL, or paste your own image URL" 
                    : "Paste an image URL or use project URL to auto-generate"
                  }
                </p>
                
                {/* Image Preview */}
                {formData.image && (
                  <div className="mt-3 border border-white/10 rounded overflow-hidden">
                    <div className="text-xs text-gray-400 px-3 py-2 bg-white/5 border-b border-white/10 flex justify-between items-center">
                      <span>Image Preview</span>
                      {formData.image.includes('thum.io') && (
                        <span className="text-purple-400 text-xs">Auto-generated</span>
                      )}
                    </div>
                    <div className="aspect-video bg-gray-900 relative">
                      {isGeneratingPreview ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                          <Loader2 size={32} className="mb-2 animate-spin text-purple-400" />
                          <span className="text-sm">Generating preview...</span>
                        </div>
                      ) : imagePreviewError ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                          <AlertCircle size={32} className="mb-2" />
                          <span className="text-sm">Failed to load image</span>
                          <span className="text-xs mt-1">Check if the URL is correct</span>
                        </div>
                      ) : (
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="w-full h-full object-contain"
                          onError={() => setImagePreviewError(true)}
                          onLoad={() => setImagePreviewError(false)}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-transparent border border-white/10 focus:border-white text-white px-3 py-2 rounded-md"
                >
                  <option value="Web Application" className="bg-black">Web Application</option>
                  <option value="Mobile Application" className="bg-black">Mobile Application</option>
                  <option value="Enterprise Software" className="bg-black">Enterprise Software</option>
                  <option value="Data Visualization" className="bg-black">Data Visualization</option>
                  <option value="IoT Solution" className="bg-black">IoT Solution</option>
                  <option value="E-commerce" className="bg-black">E-commerce</option>
                  <option value="Other" className="bg-black">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Technologies</label>
                <div className="flex space-x-2 mb-2">
                  <Input
                    value={currentTech}
                    onChange={(e) => setCurrentTech(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTech())}
                    className="flex-1 bg-transparent border-white/10 focus:border-white text-white"
                    placeholder="Add technology (e.g., React, Node.js)"
                  />
                  <Button
                    onClick={handleAddTech}
                    type="button"
                    className="bg-white text-black hover:bg-gray-200"
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tech.map((tech, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center text-sm px-3 py-1 border border-white/20 text-gray-300"
                    >
                      {tech}
                      <button
                        onClick={() => handleRemoveTech(index)}
                        className="ml-2 text-gray-400 hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  onClick={() => setIsDialogOpen(false)}
                  className="bg-white/10 text-white hover:bg-white/20 border border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-white text-black hover:bg-gray-200"
                >
                  <Save size={16} className="mr-2" />
                  Save Project
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPortfolio;
